<template>
  <div class="container">
    <BreadcrumbGroup :items="breadcrumbItems" />

    <HeadingOne>お客さま情報の変更</HeadingOne>

    <ErrorsAlert :errors="errors"></ErrorsAlert>

    <WizardGroup class="_mb-md-60 _mb-sm-20">
      <MainWizard :active-index="1" type="userinfo"></MainWizard>
    </WizardGroup>

    <p>内容をご確認のうえ、よろしければ「変更する」を押してください。</p>

    <section>
      <TableGroup>
        <HeadingTwo class="_mt-50">お客さま情報</HeadingTwo>
        <TableBox>
          <TableBody>
            <TableRow>
              <TableHeader>
                お客さま番号
                <ChangeBadgeIcon></ChangeBadgeIcon>
              </TableHeader>
              <TableData>
                {{ userData.account_id }}
              </TableData>
            </TableRow>
            <TableRow>
              <TableHeader>
                氏名<small>（漢字）</small>
                <ChangeBadgeIcon></ChangeBadgeIcon>
              </TableHeader>
              <TableData>
                {{ userData.name_kanji }}
              </TableData>
            </TableRow>
            <TableRow>
              <TableHeader>
                氏名<small>（フリガナ）</small>
                <ChangeBadgeIcon></ChangeBadgeIcon>
              </TableHeader>
              <TableData>
                {{ userData.name_kana }}
              </TableData>
            </TableRow>
            <TableRow>
              <TableHeader>
                生年月日
                <ChangeBadgeIcon></ChangeBadgeIcon>
              </TableHeader>
              <TableData>
                {{ displayBth(userData.bth) }}
              </TableData>
            </TableRow>
            <!--<TableRow>
              <TableHeader>
                性別
                <ChangeBadgeIcon></ChangeBadgeIcon>
              </TableHeader>
              <TableData>
                {{displaySex}}
              </TableData>
            </TableRow>-->
            <TableRow v-if="auUser(userData.id_type) && hasPhoneAu">
              <TableHeader>
                au携帯電話番号または<br />UQ mobile 電話番号
                <ChangeBadgeIcon></ChangeBadgeIcon>
              </TableHeader>
              <TableData>
                {{
                  displayPhoneAu(
                    userData.phone_au_1,
                    userData.phone_au_2,
                    userData.phone_au_3
                  )
                }}
              </TableData>
            </TableRow>
            <TableRow>
              <TableHeader>
                電話番号<small>（その他）</small>
                <ChangeBadgeIcon
                  :diff-keys="diffKeys"
                  :key-list="[
                    'phone_mobile_1',
                    'phone_mobile_2',
                    'phone_mobile_3',
                  ]"
                ></ChangeBadgeIcon>
              </TableHeader>
              <TableData>
                {{
                  displayPhoneMobile(
                    hasPhoneMobile,
                    userData.phone_mobile_1,
                    userData.phone_mobile_2,
                    userData.phone_mobile_3
                  )
                }}
              </TableData>
            </TableRow>
            <TableRow>
              <TableHeader>
                毎月のご返済日
                <ChangeBadgeIcon></ChangeBadgeIcon>
              </TableHeader>
              <TableData>
                毎月{{ userData.contractual_repayment_cycle }}日
              </TableData>
            </TableRow>
          </TableBody>
        </TableBox>

        <HeadingTwo class="_mt-50">ご自宅情報</HeadingTwo>
        <TableBox>
          <TableBody>
            <TableRow>
              <TableHeader>
                郵便番号
                <ChangeBadgeIcon
                  :diff-keys="diffKeys"
                  :key-list="['postcode']"
                ></ChangeBadgeIcon>
              </TableHeader>
              <TableData>
                〒{{ displayPostcode(userData.postcode) }}
              </TableData>
            </TableRow>
            <TableRow>
              <TableHeader>
                ご住所<small>（漢字）</small>
                <ChangeBadgeIcon
                  :diff-keys="diffKeys"
                  :key-list="[
                    'address_region',
                    'address_city',
                    'address_street',
                    'address_building',
                  ]"
                ></ChangeBadgeIcon>
              </TableHeader>
              <TableData>
                {{
                  displayAddress(
                    userData.address_region,
                    userData.address_city,
                    userData.address_street,
                    userData.address_building
                  )
                }}
              </TableData>
            </TableRow>
            <TableRow>
              <TableHeader>
                ご住所<small>（フリガナ）</small>
                <ChangeBadgeIcon
                  :diff-keys="diffKeys"
                  :key-list="['address_kana']"
                ></ChangeBadgeIcon>
              </TableHeader>
              <TableData>
                {{ userData.address_kana }}
              </TableData>
            </TableRow>
            <TableRow>
              <TableHeader>
                お住まいの種類
                <ChangeBadgeIcon
                  :diff-keys="diffKeys"
                  :key-list="['home_type']"
                ></ChangeBadgeIcon>
              </TableHeader>
              <TableData>
                {{ displayHomeType(userData.home_type) }}
              </TableData>
            </TableRow>
            <TableRow>
              <TableHeader>
                居住開始年月
                <ChangeBadgeIcon
                  :diff-keys="diffKeys"
                  :key-list="['nky_ngt']"
                ></ChangeBadgeIcon>
              </TableHeader>
              <TableData>
                {{ displayNkyNgt(userData.nky_ngt) }}
              </TableData>
            </TableRow>
            <TableRow>
              <TableHeader>
                住宅ローンもしくは家賃のお支払い<small>（月額）</small>
                <ChangeBadgeIcon
                  :diff-keys="diffKeys"
                  :key-list="['rent_loan_amount']"
                ></ChangeBadgeIcon>
              </TableHeader>
              <TableData>
                {{ displayRentLoanAmount(userData.rent_loan_amount) }}
              </TableData>
            </TableRow>
          </TableBody>
        </TableBox>
        <TableBox>
          <TableBody>
            <TableRow>
              <TableHeader>
                ご家族（配偶者さま）
                <ChangeBadgeIcon
                  :diff-keys="diffKeys"
                  :key-list="['marriage_status']"
                ></ChangeBadgeIcon>
              </TableHeader>
              <TableData>
                {{ displayMarriageStatus(userData.marriage_status) }}
              </TableData>
            </TableRow>
            <TableRow>
              <TableHeader>
                ご家族（お子さま）
                <ChangeBadgeIcon
                  :diff-keys="diffKeys"
                  :key-list="['number_of_children']"
                ></ChangeBadgeIcon>
              </TableHeader>
              <TableData>
                {{ displayNumberOfChildren(userData.number_of_children) }}
              </TableData>
            </TableRow>
          </TableBody>
        </TableBox>
      </TableGroup>

      <form novalidate @submit.prevent="submitProfile">
        <BtngroupDouble class="_mb-md-120 _mb-sm-40">
          <template #main>
            <BtnMain class="-main" @click.prevent="submitProfile">
              変更する
            </BtnMain>
          </template>
          <template #back>
            <BtnBack class="-back -sm" router-name="mb024"> 戻る </BtnBack>
          </template>
        </BtngroupDouble>
      </form>
    </section>
  </div>
</template>

<script>
import { initCredentials } from "@/utils/Auth";
import {
  getUserDataForMemberLambdaInvoke,
  submitEditProfileLambdaInvoke,
} from "@/utils/LambdaHelper";
import { setMeta } from "@/utils/Meta";
import { diffKeysGet } from "@/utils/ChangeIcon";
import { goToErrorPage, scrollTop } from "@/utils/Other";
import {
  displayBth,
  displayPhoneAu,
  displayPhoneMobile,
  displayPostcode,
  displayAddress,
  displayHomeType,
  displayNkyNgt,
  displayRentLoanAmount,
  displayMarriageStatus,
  displayNumberOfChildren,
  auUser,
  setHasPhoneAu,
  setHasPhoneMobile,
} from "@/utils/UserData";

import TableGroup from "@/components/tables/TableGroup";
import TableBox from "@/components/tables/TableBox";
import TableBody from "@/components/tables/TableBody";
import TableRow from "@/components/tables/TableRow";
import TableHeader from "@/components/tables/TableHeader";
import TableData from "@/components/tables/TableData";
import HeadingOne from "@/components/heading/HeadingOne";
import HeadingTwo from "@/components/heading/HeadingTwo";
import BtnMain from "@/components/button/BtnMain";
import BtnBack from "@/components/button/BtnBack";
import BtngroupDouble from "@/components/button/BtngroupDouble";
import MainWizard from "@/components/wizard/MainWizard";
import WizardGroup from "@/components/wizard/WizardGroup";
import ErrorsAlert from "@/components/alert/ErrorsAlert";
import ChangeBadgeIcon from "@/components/badges/ChangeBadgeIcon";
import BreadcrumbGroup from "@/components/breadcrumb/BreadcrumbGroup";

export default {
  name: "Mb027",
  metaInfo: {
    title: "お客さま情報の変更｜お客さま情報の確認・変更",
    meta: [
      {
        name: "description",
        content:
          "au PAY スマートローンはau IDをお持ちのお客様のための便利なカードレスローンサービスです。お申込みは、スマートフォン・パソコンでかんたんWEB完結！24時間お申込み可能。最短30分で審査結果をご連絡します。",
      },
      {
        name: "keywords",
        content:
          "au PAY スマートローン,au,カードレスローン,スマートフォン,au PAY 残高,セブン銀行スマホATM,借入,返済,手続き,会員,会員サイト,お客さま情報",
      },
    ],
  },
  components: {
    ChangeBadgeIcon,
    ErrorsAlert,
    TableGroup,
    TableData,
    TableHeader,
    TableRow,
    TableBody,
    TableBox,
    HeadingOne,
    HeadingTwo,
    BtnMain,
    BtnBack,
    BtngroupDouble,
    WizardGroup,
    MainWizard,
    BreadcrumbGroup,
  },
  setup() {
    setMeta({
      title: "au PAY スマートローン",
      description:
        "au PAY スマートローンはau IDをお持ちのお客様のための便利なカードレスローンサービスです。お申込みは、スマートフォン・パソコンでかんたんWEB完結！24時間お申込み可能。最短30分で審査結果をご連絡します。",
      keywords:
        "au PAY スマートローン,au,カードレスローン,スマートフォン,au PAY 残高,セブン銀行スマホATM,借入,返済,手続き",
    });

    return {
      displayBth,
      displayPhoneAu,
      displayPhoneMobile,
      displayPostcode,
      displayAddress,
      displayHomeType,
      displayNkyNgt,
      displayRentLoanAmount,
      displayMarriageStatus,
      displayNumberOfChildren,
      auUser,
      setHasPhoneAu,
      setHasPhoneMobile,
    };
  },
  data() {
    return {
      breadcrumbItems: [
        {
          text: "トップ",
          routerName: "mb001",
        },
        {
          text: "お客さま情報の確認・変更",
          routerName: "mb023",
        },
        {
          text: "お客さま情報の変更",
          active: true,
        },
      ],
      errors: {},
      isDisabled: false,
      hasPhoneMobile: false,
      hasPhoneAu: false,
      userData: {
        id_type: "",
        account_id: "",
        name_kanji: "",
        name_kana: "",
        firstname_kana: "",
        firstname_kanji: "",
        lastname_kana: "",
        lastname_kanji: "",
        bth: "",
        sex: "",
        phone_au_1: "",
        phone_au_2: "",
        phone_au_3: "",
        phone_mobile_1: "",
        phone_mobile_2: "",
        phone_mobile_3: "",
        repayment_date: "",
        contractual_repayment_cycle: "",
        postcode: "",
        address_region: "",
        address_city: "",
        address_street: "",
        address_building: "",
        address_kana: "",
        home_type: "",
        nky_ngt: "",
        rent_loan_amount: "",
        marriage_status: "",
        number_of_children: "",
      },
      diffKeys: [],
    };
  },
  mounted() {
    try {
      getUserDataForMemberLambdaInvoke()
        .then((res) => {
          for (let key in res) {
            if (Object.prototype.hasOwnProperty.call(this.userData, key)) {
              this.userData[key] = res[key].toString().trim();
            }
          }
          this.hasPhoneAu = setHasPhoneAu(
            this.userData.phone_au_1,
            this.userData.phone_au_2,
            this.userData.phone_au_3
          );
          this.hasPhoneMobile = setHasPhoneMobile(
            this.userData.phone_mobile_1,
            this.userData.phone_mobile_2,
            this.userData.phone_mobile_3
          );
        })
        .catch((error) => {
          goToErrorPage(error);
        });

      diffKeysGet()
        .then((res) => {
          this.diffKeys = res;
        })
        .catch((error) => {
          goToErrorPage(error);
        });
    } catch (e) {
      goToErrorPage(e);
    }
  },
  methods: {
    submitProfile: function () {
      try {
        if (this.isDisabled) {
          return;
        }
        this.isDisabled = true;
        this.errors = {};
        initCredentials()
          .then(() => {
            submitEditProfileLambdaInvoke()
              .then((res) => {
                this.errors = res.errors || {};
                if (Object.keys(this.errors).length > 0) {
                  scrollTop();
                  this.isDisabled = false;
                } else {
                  this.$router.push({ name: "tem017" });
                }
              })
              .catch((error) => {
                goToErrorPage(error);
              });
          })
          .catch((error) => {
            goToErrorPage(error);
          });
      } catch (e) {
        goToErrorPage(e);
      }
    },
  },
};
</script>

<style scoped></style>
